import { useCallback, useEffect, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useBooks } from "./BooksProvider";
import { useLoad } from "./useLoad";
import type { Book, MonthlySummaryMonth, ReportingBasis } from "./types";

/** A calendar month as the URL carries it: "2026-09". */
export type Month = string;

const pad = (n: number) => String(n).padStart(2, "0");

export const monthOf = (date: string): Month => date.slice(0, 7);

export function addMonths(month: Month, n: number): Month {
  const [y, m] = month.split("-").map(Number);
  const i = y! * 12 + (m! - 1) + n;
  return `${Math.floor(i / 12)}-${pad((i % 12) + 1)}`;
}

/** A YYYY-MM-DD date `n` days on (or back, for negative `n`), in UTC so no timezone shifts the day. */
export function addDays(iso: string, n: number) {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Inclusive, oldest first. */
export function monthsBetween(from: Month, to: Month): Month[] {
  const out: Month[] = [];
  for (let m = from; m <= to && out.length < 600; m = addMonths(m, 1)) out.push(m);
  return out;
}

export function lastDay(month: Month): string {
  const [y, m] = month.split("-").map(Number);
  return `${month}-${pad(new Date(Date.UTC(y!, m!, 0)).getUTCDate())}`;
}

const isMonth = (v: string | null): v is Month => !!v && /^\d{4}-(0[1-9]|1[0-2])$/.test(v);

export const thisMonth = (now = new Date()): Month => `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;

export const todayIso = (now = new Date()) => `${thisMonth(now)}-${pad(now.getDate())}`;

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function monthLabel(month: Month, withYear = true) {
  const [y, m] = month.split("-").map(Number);
  return withYear ? `${MONTH_NAMES[m! - 1]} ${y}` : MONTH_NAMES[m! - 1]!;
}

export function periodLabel(from: Month, to: Month) {
  return from === to ? monthLabel(from) : `${monthLabel(from)} – ${monthLabel(to)}`;
}

/** The first month of the financial year that contains `month`, for a year starting in `fyStart` (1-12). */
export function financialYearStart(month: Month, fyStart: number): Month {
  const m = Number(month.slice(5, 7));
  const back = (m - fyStart + 12) % 12;
  return addMonths(month, -back);
}

export type PresetId = "this-month" | "last-month" | "this-quarter" | "ytd" | "last-fy" | "all";

export const PRESETS: Array<{ id: PresetId; label: string }> = [
  { id: "this-month", label: "This month" },
  { id: "last-month", label: "Last month" },
  { id: "this-quarter", label: "This quarter" },
  { id: "ytd", label: "Year to date" },
  { id: "last-fy", label: "Last financial year" },
  { id: "all", label: "All" },
];

/** A preset's range inside [first, current], or null when it lies wholly outside the books. */
export function presetRange(id: PresetId, first: Month, current: Month, fyStart: number): [Month, Month] | null {
  const fy = financialYearStart(current, fyStart);
  const raw: [Month, Month] = (() => {
    switch (id) {
      case "this-month":
        return [current, current];
      case "last-month":
        return [addMonths(current, -1), addMonths(current, -1)];
      case "this-quarter": {
        // Quarters of the financial year, so they line up with the year's own reporting.
        const into = monthsBetween(fy, current).length - 1;
        return [addMonths(fy, into - (into % 3)), current];
      }
      case "ytd":
        return [fy, current];
      case "last-fy":
        return [addMonths(fy, -12), addMonths(fy, -1)];
      case "all":
        return [first, current];
    }
  })();
  return clamp(raw[0], raw[1], first, current);
}

/** Into [first, current], in order; null when nothing of it is left. */
export function clamp(from: Month, to: Month, first: Month, current: Month): [Month, Month] | null {
  let [a, b] = from <= to ? [from, to] : [to, from];
  if (b < first || a > current) return null;
  if (a < first) a = first;
  if (b > current) b = current;
  return [a, b];
}

export interface Period {
  from: Month;
  to: Month;
  /** First and last day, YYYY-MM-DD, for range reads. */
  fromDate: string;
  toDate: string;
  /** As-at date for balance reads: the period's last day, or today while the period includes this month. */
  asAt: string;
  label: string;
  /** Every month the slider offers: the book's start month to this month. */
  months: Month[];
  fyStart: number;
  set: (from: Month, to: Month) => void;
  /** "from=…&to=…", for links that carry the period to another Books page. */
  query: string;
  /** Accrual or cash: how the reports count invoices and bills. Travels with the period, defaulting to the book's setting. */
  basis: ReportingBasis;
  setBasis: (basis: ReportingBasis) => void;
}

const STORAGE_KEY = "dolfin-books-period";

function remembered(bookId: string | undefined): [Month, Month] | null {
  try {
    const v = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? "null");
    return v && v.bookId === bookId && isMonth(v.from) && isMonth(v.to) ? [v.from, v.to] : null;
  } catch {
    return null;
  }
}

function remember(bookId: string | undefined, from: Month, to: Month) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ bookId, from, to }));
  } catch {
    // Private mode or blocked storage: the URL still carries the period.
  }
}

const BASIS_KEY = "dolfin-books-basis";
const isBasis = (v: unknown): v is ReportingBasis => v === "Accrual" || v === "Cash";

function rememberedBasis(bookId: string | undefined): ReportingBasis | null {
  try {
    const v = JSON.parse(sessionStorage.getItem(BASIS_KEY) ?? "null");
    return v && v.bookId === bookId && isBasis(v.basis) ? v.basis : null;
  } catch {
    return null;
  }
}

function rememberBasis(bookId: string | undefined, basis: ReportingBasis) {
  try {
    sessionStorage.setItem(BASIS_KEY, JSON.stringify({ bookId, basis }));
  } catch {
    // Private mode or blocked storage: the URL still carries the basis.
  }
}

// One read per platform client, however many pages ask: only the financial year's start month is needed.
const fyStartCache = new WeakMap<object, Promise<{ data?: number; response: Response }>>();

/** The financial year's first month, and whether it has been read (until then 1 is only a placeholder). */
function useFinancialYearStart(): { fyStart: number; loaded: boolean } {
  const { platform } = useBooks();
  const fy = useLoad<number>(() => {
    if (!fyStartCache.has(platform)) {
      fyStartCache.set(
        platform,
        platform.GET("/v1/accounting/connection", {}).then(({ data, response }) => ({
          data: Number(data?.financialYearStartMonth) || 1,
          response,
        })),
      );
    }
    return fyStartCache.get(platform)!;
  }, [platform]);
  return { fyStart: fy.data ?? 1, loaded: !fy.loading };
}

/**
 * D49: the one Books period, in the URL as ?from=YYYY-MM&to=YYYY-MM. Without one it is the last period used in this
 * tab, else the financial year to date, clamped to the book's start. Writes push history, so back/forward step
 * through periods.
 */
export function usePeriod(book: Book | null | undefined): Period {
  const [params, setParams] = useSearchParams();
  const { fyStart, loaded: fyLoaded } = useFinancialYearStart();
  const current = thisMonth();
  const first = book?.startDate ? monthOf(book.startDate) : current;
  const months = useMemo(() => monthsBetween(first, current), [first, current]);

  const fromParam = params.get("from");
  const toParam = params.get("to");
  const fromUrl = isMonth(fromParam) && isMonth(toParam) ? clamp(fromParam, toParam, first, current) : null;
  const fallback = remembered(book?.id) ?? presetRange("ytd", first, current, fyStart) ?? [first, current];
  const [from, to] = fromUrl ?? clamp(fallback[0], fallback[1], first, current) ?? [first, current];

  const set = useCallback(
    (f: Month, t: Month) => {
      const range = clamp(f, t, first, current);
      if (!range) return;
      remember(book?.id, range[0], range[1]);
      setParams((p) => {
        p.set("from", range[0]);
        p.set("to", range[1]);
        return p;
      });
    },
    [first, current, book?.id, setParams],
  );

  // Arriving without a period (a nav link, a bookmark): write the one in force, so links and reloads keep it. Not before
  // the financial year is known, or a calendar-year placeholder would be written and remembered for the tab.
  useEffect(() => {
    if (book && fyLoaded && !fromUrl) {
      setParams(
        (p) => {
          p.set("from", from);
          p.set("to", to);
          return p;
        },
        { replace: true },
      );
    }
  }, [book, fyLoaded, fromUrl, from, to, setParams]);

  useEffect(() => {
    if (book && fromUrl) remember(book.id, from, to);
  }, [book, fromUrl, from, to]);

  // Like the period: the URL, else the last basis used in this tab, else the book's default.
  const basisParam = params.get("basis");
  const basis: ReportingBasis = isBasis(basisParam)
    ? basisParam
    : (rememberedBasis(book?.id) ?? (isBasis(book?.reportingBasis) ? book.reportingBasis : "Accrual"));
  const setBasis = useCallback(
    (b: ReportingBasis) => {
      rememberBasis(book?.id, b);
      setParams((p) => {
        p.set("basis", b);
        return p;
      });
    },
    [book?.id, setParams],
  );
  useEffect(() => {
    if (book && isBasis(basisParam)) rememberBasis(book.id, basisParam);
  }, [book, basisParam]);

  return {
    from,
    to,
    fromDate: `${from}-01`,
    toDate: lastDay(to),
    asAt: to === current ? todayIso() : lastDay(to),
    label: periodLabel(from, to),
    months,
    fyStart,
    set,
    query: `from=${from}&to=${to}`,
    basis,
    setBasis,
  };
}

/** Adds the current page's period to a Books link, so moving between pages keeps it. */
export function usePeriodLink() {
  const { search } = useLocation();
  return useCallback(
    (href: string) => {
      const here = new URLSearchParams(search);
      const from = here.get("from");
      const to = here.get("to");
      const basis = here.get("basis");
      const [path, query = ""] = href.split("?");
      const next = new URLSearchParams(query);
      if (isMonth(from) && isMonth(to)) {
        next.set("from", from);
        next.set("to", to);
      }
      if (isBasis(basis) && !next.has("basis")) next.set("basis", basis);
      const out = next.toString();
      return out ? `${path}?${out}` : path!;
    },
    [search],
  );
}

/** Every month of the books with its income, expenses and net (P&L classification) on a basis, zero-filled. */
export function useMonthlySummary(basis: ReportingBasis) {
  const { books } = useBooks();
  return useLoad(() => books.GET("/v1/books/current/monthly-summary", { params: { query: { Basis: basis } } }), [books, basis]);
}

/** The period's totals from the monthly figures: they match the P&L for the same months. */
export function periodTotals(months: MonthlySummaryMonth[] | undefined, period: Pick<Period, "from" | "to">) {
  const inside = (months ?? []).filter((m) => {
    const mm = (m.month ?? "").slice(0, 7);
    return mm >= period.from && mm <= period.to;
  });
  const sum = (key: "income" | "expenses" | "net") => inside.reduce((s, m) => s + Number(m[key] ?? 0), 0);
  return { income: sum("income"), expenses: sum("expenses"), net: sum("net") };
}
